import { apiFetch } from "@/lib/auth-api";
import type {
	ApplyStrategyRequest,
	ApplyStrategyResponse,
	CentralPricingQueryParams,
	CentralPricingResponse,
	CreateModifierRequest,
	ExclusionItem,
	ExclusionOverrideRequest,
	PriceModifierItem,
	PricingQueryParams,
	PricingResponse,
	ProductStrategyOverride,
	ResultsQueryParams,
	StrategConfig,
	StrategyResultDetail,
	StrategyResultsResponse,
	StrategyStatistics,
	UpsertOverrideInput,
} from "./types";

const BASE = "/strateg-cenowy";

// ── Pharmacies ───────────────────────────────────────────────────────────────

export interface PharmacyItem {
	idfirm: number;
	name: string;
	city?: string;
}

export async function fetchPharmacies(): Promise<PharmacyItem[]> {
	return apiFetch<PharmacyItem[]>(`${BASE}/pharmacies`);
}

// ── Exclusion count ──────────────────────────────────────────────────────────

export async function fetchExclusionCount(idfirm?: number): Promise<{ count: number }> {
	const qs = idfirm ? `?idfirm=${idfirm}` : "";
	return apiFetch<{ count: number }>(`${BASE}/exclusion-count${qs}`);
}

// ── Results ──────────────────────────────────────────────────────────────────

export async function fetchResults(params: ResultsQueryParams): Promise<StrategyResultsResponse> {
	const searchParams = new URLSearchParams();
	if (params.scope) searchParams.set("scope", params.scope);
	if (params.idfirm) searchParams.set("idfirm", String(params.idfirm));
	if (params.search) searchParams.set("search", params.search);
	if (params.category) searchParams.set("category", params.category);
	if (params.primaryCategoryCode)
		searchParams.set("primaryCategoryCode", params.primaryCategoryCode);
	if (params.tagId) searchParams.set("tagId", String(params.tagId));
	if (params.showExcluded) searchParams.set("showExcluded", "true");
	if (params.showNoData) searchParams.set("showNoData", "true");
	if (params.strategy) searchParams.set("strategy", params.strategy);
	if (params.priceAction) searchParams.set("priceAction", params.priceAction);
	if (params.mechanism) searchParams.set("mechanism", params.mechanism);
	if (params.sourceCode) searchParams.set("sourceCode", params.sourceCode);
	if (params.ownPriceList) searchParams.set("ownPriceList", "true");
	if (params.sortBy) searchParams.set("sortBy", params.sortBy);
	if (params.sortDir) searchParams.set("sortDir", params.sortDir);
	if (params.limit) searchParams.set("limit", String(params.limit));
	if (params.offset) searchParams.set("offset", String(params.offset));
	if (params.period) searchParams.set("period", String(params.period));
	const qs = searchParams.toString();
	return apiFetch(`${BASE}/results${qs ? `?${qs}` : ""}`);
}

export async function fetchResultDetail(
	bloz07: string,
	scope?: string,
): Promise<StrategyResultDetail> {
	const qs = scope ? `?scope=${encodeURIComponent(scope)}` : "";
	return apiFetch<StrategyResultDetail>(`${BASE}/results/${encodeURIComponent(bloz07)}${qs}`);
}

// ── Zalegacze (partie >= 91 dni) ─────────────────────────────────────────────

export interface ZalegaczBatchRow {
	seria: string | null;
	datzk: string;
	ilakt: number;
	dniZalegania: number;
}

export interface ZalegaczBatchesResponse {
	items: ZalegaczBatchRow[];
	totalUnits: number;
	totalBatches: number;
	snapshotDate: string | null;
}

export async function fetchZalegaczBatches(
	bloz07: string,
	idfirm: number,
): Promise<ZalegaczBatchesResponse> {
	return apiFetch<ZalegaczBatchesResponse>(
		`${BASE}/results/${encodeURIComponent(bloz07)}/zalegacze-batches?idfirm=${idfirm}`,
	);
}

/** Object-style API for direct imports (used by wyniki.tsx, detail drawer) */
export const strategCenowyApi = {
	getResults: fetchResults,
	getDetail: fetchResultDetail,

	/**
	 * PUT /strateg-cenowy/products/{bloz07}/strategy?scope=...
	 * Optimistic concurrency przez `If-Match: <version>` (gdy podane).
	 */
	async setStrategyOverride(
		bloz07: string,
		scope: string,
		input: UpsertOverrideInput,
		ifMatch?: number,
	): Promise<ProductStrategyOverride> {
		const headers: Record<string, string> = {};
		if (ifMatch !== undefined) headers["If-Match"] = String(ifMatch);
		return apiFetch<ProductStrategyOverride>(
			`${BASE}/products/${encodeURIComponent(bloz07)}/strategy?scope=${encodeURIComponent(scope)}`,
			{
				method: "PUT",
				headers,
				body: JSON.stringify(input),
			},
		);
	},

	/**
	 * DELETE /strateg-cenowy/products/{bloz07}/strategy?scope=...
	 * Usuwa override per-product. If-Match opcjonalne.
	 */
	async clearStrategyOverride(bloz07: string, scope: string, ifMatch?: number): Promise<void> {
		const headers: Record<string, string> = {};
		if (ifMatch !== undefined) headers["If-Match"] = String(ifMatch);
		await apiFetch<void>(
			`${BASE}/products/${encodeURIComponent(bloz07)}/strategy?scope=${encodeURIComponent(scope)}`,
			{ method: "DELETE", headers },
		);
	},

	/**
	 * POST /strateg-cenowy/results/apply-strategy?scope=...
	 * Masowe ustawienie strategii dla wszystkich produktow pasujacych do filtrow.
	 */
	async applyStrategyBulk(
		scope: string,
		body: ApplyStrategyRequest,
	): Promise<ApplyStrategyResponse> {
		return apiFetch<ApplyStrategyResponse>(
			`${BASE}/results/apply-strategy?scope=${encodeURIComponent(scope)}`,
			{ method: "POST", body: JSON.stringify(body) },
		);
	},
};

// ── Statistics ───────────────────────────────────────────────────────────────

export async function fetchStatistics(params?: {
	scope?: string;
	idfirm?: number;
	strategy?: string;
}): Promise<StrategyStatistics> {
	const searchParams = new URLSearchParams();
	if (params?.scope) searchParams.set("scope", params.scope);
	if (params?.idfirm) searchParams.set("idfirm", String(params.idfirm));
	if (params?.strategy) searchParams.set("strategy", params.strategy);
	const qs = searchParams.toString();
	return apiFetch(`${BASE}/statistics${qs ? `?${qs}` : ""}`);
}

// ── Config ───────────────────────────────────────────────────────────────────

export async function fetchConfig(scope: string): Promise<StrategConfig> {
	return apiFetch(`${BASE}/config?scope=${encodeURIComponent(scope)}`);
}

export async function updateConfig(
	scope: string,
	data: Partial<StrategConfig>,
): Promise<StrategConfig> {
	return apiFetch(`${BASE}/config?scope=${encodeURIComponent(scope)}`, {
		method: "PUT",
		body: JSON.stringify(data),
	});
}

export async function deleteConfigOverride(scope: string): Promise<StrategConfig> {
	return apiFetch(`${BASE}/config?scope=${encodeURIComponent(scope)}`, { method: "DELETE" });
}

// ── Exclusions ───────────────────────────────────────────────────────────────

export async function fetchExclusions(idfirm?: number): Promise<ExclusionItem[]> {
	const qs = idfirm ? `?idfirm=${idfirm}` : "";
	return apiFetch(`${BASE}/exclusions${qs}`);
}

export async function createExclusionOverride(data: ExclusionOverrideRequest) {
	return apiFetch(`${BASE}/exclusions/override`, {
		method: "POST",
		body: JSON.stringify(data),
	});
}

export async function deleteExclusionOverride(id: number) {
	return apiFetch(`${BASE}/exclusions/override/${id}`, { method: "DELETE" });
}

// ── Modifiers ────────────────────────────────────────────────────────────────

export async function fetchModifiers(type?: string): Promise<PriceModifierItem[]> {
	const qs = type ? `?type=${type}` : "";
	return apiFetch(`${BASE}/modifiers${qs}`);
}

export async function createModifier(data: CreateModifierRequest) {
	return apiFetch(`${BASE}/modifiers`, { method: "POST", body: JSON.stringify(data) });
}

export async function updateModifier(id: number, data: CreateModifierRequest) {
	return apiFetch(`${BASE}/modifiers/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteModifier(id: number) {
	return apiFetch(`${BASE}/modifiers/${id}`, { method: "DELETE" });
}

// ── Decyl import (manual upload + reload from MOVEit) ────────────────────────

export interface ImportDecylResponse {
	status: "ok" | "duplicate";
	rowCount: number;
	reportPeriod: string;
	reportId: number;
	scope?: string;
	fileName?: string;
}

export interface DecylDownloadItem {
	id: number;
	fileName: string;
	fileSize: number | null;
	downloadedAt: string | null;
	status: "downloaded" | "processed" | "process_error" | "deferred" | "error";
	sourceId: string;
	accountId: string;
	isReloadable: boolean;
	kind: "global" | "per_pharmacy" | "other";
	period: string | null;
	unitCode: string | null;
	errorMessage: string | null;
}

/** Manual upload — multipart, super_admin only. */
export async function triggerImportDecyl(file: File): Promise<ImportDecylResponse> {
	const formData = new FormData();
	formData.append("file", file);
	return apiFetch<ImportDecylResponse>(`${BASE}/import-decyl`, {
		method: "POST",
		body: formData,
	});
}

/** Lista plików decyl pobranych przez MOVEit sync (downloaded + processed + deferred). */
export async function fetchDecylDownloads(includeProcessed = true): Promise<DecylDownloadItem[]> {
	const qs = includeProcessed ? "?includeProcessed=true" : "?includeProcessed=false";
	return apiFetch<DecylDownloadItem[]>(`${BASE}/decyl-downloads${qs}`);
}

/** Reload-from-disk — wczytuje istniejacy plik z MOVEit (nadpisuje stary raport za period). */
export async function importDecylFromDisk(downloadId: number): Promise<ImportDecylResponse> {
	return apiFetch<ImportDecylResponse>(`${BASE}/import-decyl-from-disk/${downloadId}`, {
		method: "POST",
	});
}

// ── Pricing (Cenniki wlasne) ─────────────────────────────────────────────────

/**
 * GET /api/strateg-cenowy/pricing — pozycje cennikow aptecznych dla scope.
 * Backend filtruje przez `visible_idfirms` ∩ `resolve_scope_to_idfirms`.
 * Domyslnie zwraca tylko aktywne i aktualnie obowiazujace cenniki (toggle
 * `includeArchived=true` aby pokazac historyczne/przyszle).
 */
export async function fetchPricing(params: PricingQueryParams): Promise<PricingResponse> {
	const qs = new URLSearchParams();
	qs.set("scope", params.scope);
	if (params.search) qs.set("search", params.search);
	if (params.sort) qs.set("sort", params.sort);
	if (params.dir) qs.set("dir", params.dir);
	if (params.limit !== undefined) qs.set("limit", String(params.limit));
	if (params.offset !== undefined) qs.set("offset", String(params.offset));
	return apiFetch<PricingResponse>(`${BASE}/pricing?${qs.toString()}`);
}

/**
 * GET /api/strateg-cenowy/central-pricing — cenniki centralne/hurtowni
 * (cennik_apteczny_cnkn_item), inna semantyka niz fetchPricing (ten ostatni
 * pokazuje cenniki WLASNE aptek z cnkp+cnkn idsys=11).
 */
export async function fetchCentralPricing(
	params: CentralPricingQueryParams,
): Promise<CentralPricingResponse> {
	const qs = new URLSearchParams();
	qs.set("scope", params.scope);
	if (params.includeArchived) qs.set("includeArchived", "true");
	if (params.search) qs.set("search", params.search);
	if (params.sort) qs.set("sort", params.sort);
	if (params.dir) qs.set("dir", params.dir);
	if (params.limit !== undefined) qs.set("limit", String(params.limit));
	if (params.offset !== undefined) qs.set("offset", String(params.offset));
	return apiFetch<CentralPricingResponse>(`${BASE}/central-pricing?${qs.toString()}`);
}
